import React, { useState } from 'react';
import { tweetService, Tweet } from '../services/tweetService';
import { styleService } from '../services/styleService';
import { buzzService } from '../services/buzzService';
import { ArchiveUploadPrompt } from './ArchiveUploadPrompt';
import { demoStylePrompt, demoBuzzInsights } from '../data/demoData';
import { useT } from '../lib/i18n';

export const SetupFlow: React.FC = () => {
  const { t } = useT();
  const [status, setStatus] = useState<'upload' | 'fetching' | 'analyzing' | 'complete' | 'error'>('upload');
  const [progress, setProgress] = useState(0);
  const [tweetCount, setTweetCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [, setFetchedTweets] = useState<Tweet[]>([]);
  const [buzzInsights, setBuzzInsights] = useState<string[]>([]);

  const handleArchiveUpload = (tweets: Tweet[], isDemo: boolean = false) => {
    setFetchedTweets(tweets);
    setTweetCount(tweets.length);
    setStatus('analyzing');
    startDemoAnalysis(tweets, isDemo);
  };

  const handleSkipToOAuth = () => {
    setStatus('fetching');
    startSetupWithOAuth();
  };

  const startDemoAnalysis = async (tweets: Tweet[], isDemo: boolean) => {
    try {
      const userId = isDemo ? 'demo_user' : 'archive_user';
      const userName = isDemo ? 'Demo User' : 'Archive User';
      const user = { id: userId, name: userName };
      localStorage.setItem('user', JSON.stringify(user));

      setProgress(30);
      await new Promise(resolve => setTimeout(resolve, 1000));

      const styleModel = {
        systemPrompt: demoStylePrompt,
        exampleCount: tweets.length,
        createdAt: new Date().toISOString(),
        userId
      };
      localStorage.setItem('style_model', JSON.stringify(styleModel));
      setProgress(60);

      await new Promise(resolve => setTimeout(resolve, 1000));

      const buzzModel = {
        insights: demoBuzzInsights,
        analyzedTweetCount: Math.floor(tweets.length * 0.2),
        thresholdEngagement: 300,
        createdAt: new Date().toISOString(),
        userId
      };
      setBuzzInsights(buzzModel.insights);
      localStorage.setItem('buzz_model', JSON.stringify(buzzModel));
      setProgress(90);

      await new Promise(resolve => setTimeout(resolve, 500));
      setProgress(100);
      setStatus('complete');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const startSetupWithOAuth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      if (!token || !user.id) {
        throw new Error(t('setupFlow.authError'));
      }

      setStatus('fetching');
      setProgress(10);

      const result = await tweetService.fetchUserTweets(token, user.id);
      setFetchedTweets(result.tweets);
      setTweetCount(result.count);
      setProgress(40);

      await startAnalysis(result.tweets);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const startAnalysis = async (tweets: Tweet[]) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 'archive_user';

      setStatus('analyzing');
      setProgress(50);

      const styleResult = await styleService.analyzeStyle(userId, tweets);
      const styleModel = { ...styleResult.styleModel, userId };
      localStorage.setItem('style_model', JSON.stringify(styleModel));
      setProgress(60);

      setProgress(70);
      const buzzResult = await buzzService.analyzeBuzz(userId, tweets);
      const buzzModel = { ...buzzResult.buzzModel, userId };
      setBuzzInsights(buzzModel.insights);
      localStorage.setItem('buzz_model', JSON.stringify(buzzModel));
      setProgress(90);

      await new Promise((resolve) => setTimeout(resolve, 500));
      setProgress(100);
      setStatus('complete');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  return (
    <>
      {status === 'upload' && (
        <ArchiveUploadPrompt
          onUploadComplete={handleArchiveUpload}
          onSkip={handleSkipToOAuth}
        />
      )}

      <div className="min-h-screen bg-bg flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-text-primary mb-2 tracking-tight text-balance">
              {t('setupFlow.title')}
            </h1>
            <p className="text-text-secondary text-sm sm:text-[15px] leading-relaxed">
              {t('setupFlow.subtitle')}
            </p>
          </div>

          {(status === 'fetching' || status === 'analyzing') && (
            <div className="bg-surface-2 border border-border rounded-2xl p-6 sm:p-8">
              <div className="w-full h-2.5 bg-border rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-brand transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center text-2xl font-bold text-brand mb-6 tabular-nums">
                {progress}%
              </p>

              {status === 'fetching' && (
                <div className="text-center">
                  <svg className="w-10 h-10 mx-auto mb-4 text-brand animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                  <h3 className="text-xl sm:text-2xl font-bold text-text-primary mb-2 text-balance">
                    {t('setupFlow.fetching.title')}
                  </h3>
                  <p className="text-text-muted">
                    {t('setupFlow.fetching.desc')}
                  </p>
                </div>
              )}

              {status === 'analyzing' && (
                <div className="text-center">
                  {progress < 70 ? (
                    <>
                      <svg className="w-10 h-10 mx-auto mb-4 text-accent animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /><path d="M8 12h.01" /><path d="M16 12h.01" /><path d="M9 9l1.5 1.5" /><path d="M13.5 13.5L15 15" /><path d="M15 9l-1.5 1.5" /><path d="M10.5 13.5L9 15" /></svg>
                      <h3 className="text-xl sm:text-2xl font-bold text-text-primary mb-2 text-balance">
                        {t('setupFlow.analyzingStyle.title')}
                      </h3>
                      <p className="text-text-muted">
                        {t('setupFlow.analyzingStyle.desc', { count: tweetCount })}
                      </p>
                    </>
                  ) : (
                    <>
                      <svg className="w-10 h-10 mx-auto mb-4 text-emerald-400 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                      <h3 className="text-xl sm:text-2xl font-bold text-text-primary mb-2 text-balance">
                        {t('setupFlow.analyzingBuzz.title')}
                      </h3>
                      <p className="text-text-muted">
                        {t('setupFlow.analyzingBuzz.desc')}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {status === 'complete' && (
            <div className="space-y-6">
              <div className="bg-surface-2 border border-border rounded-2xl p-6 sm:p-8 text-center">
                <svg className="w-12 h-12 mx-auto mb-4 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6 text-balance">
                  {t('setupFlow.complete.title')}
                </h2>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-center gap-3 text-text-primary">
                    <svg className="w-6 h-6 text-brand" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 20V10M12 20V4M6 20v-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    <span className="text-lg">{t('setupFlow.complete.tweetsAnalyzed', { count: tweetCount })}</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-text-primary">
                    <svg className="w-6 h-6 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>
                    <span className="text-lg">{t('setupFlow.complete.styleModel')}</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-text-primary">
                    <svg className="w-6 h-6 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                    <span className="text-lg">{t('setupFlow.complete.buzzPatterns')}</span>
                  </div>
                </div>

                <button
                  onClick={() => (window.location.href = '/brain-dump')}
                  className="w-full sm:w-auto px-8 py-4 bg-brand text-white text-lg font-bold rounded-full hover:bg-brand-hover transition-all duration-200 shadow-glow-brand hover:shadow-[0_0_40px_rgba(29,161,242,0.35)] active:scale-[0.98]"
                >
                  {t('setupFlow.complete.startButton')}
                </button>
              </div>

              {buzzInsights.length > 0 && (
                <div className="bg-surface-2 border border-border rounded-2xl p-6 sm:p-8">
                  <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                    <span>{t('setupFlow.complete.buzzTitle')}</span>
                  </h3>
                  <ul className="space-y-3">
                    {buzzInsights.map((insight, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-text-primary bg-bg/50 p-3 rounded-lg"
                      >
                        <span className="text-brand font-bold flex-shrink-0">
                          {index + 1}.
                        </span>
                        <span className="leading-relaxed">{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {status === 'error' && (
            <div className="bg-surface-2 border-2 border-danger rounded-2xl p-6 sm:p-8">
              <div className="text-center mb-6">
                <svg className="w-12 h-12 mx-auto mb-4 text-danger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                <h2 className="text-2xl font-bold text-text-primary mb-2">
                  {t('setupFlow.error.title')}
                </h2>
              </div>

              <div className="bg-danger/10 border border-danger/30 rounded-xl p-4 mb-6">
                <p className="text-danger font-medium text-center">
                  {error}
                </p>
              </div>

              <div className="bg-bg/50 rounded-xl p-4 mb-6">
                <p className="text-text-muted text-sm mb-2 font-medium">
                  {t('setupFlow.error.commonCauses')}
                </p>
                <ul className="text-text-muted text-sm space-y-1 ml-4">
                  <li>• {t('setupFlow.error.invalidKey')}</li>
                  <li>• {t('setupFlow.error.networkIssue')}</li>
                  <li>• {t('setupFlow.error.tooFewTweets')}</li>
                </ul>
              </div>

              <button
                onClick={() => window.location.reload()}
                className="w-full py-3 px-4 bg-brand text-white font-bold rounded-xl hover:bg-brand/90 transition-all"
              >
                {t('setupFlow.error.retryButton')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
